/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../../config/enum';
import DefaultApiParameters from '../../../enum/DefaultApiParameters';
import { ApiRequest } from '../../../interface/common.interface';
import {
  CommonFilterProperties,
  FilterUpdateProperties,
} from '../../../interface/filter.interface';
import {
  ClearFilters,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../../shared-functions/CommonFilters';
import ocpiApi from '../../../utils/api/ocpi';

/**
 * Dispatches a notification message using the shared notification slice's action type.
 * Avoids a circular import on the notification slice from within this one.
 * @param {any} dispatch - The thunk dispatch function.
 * @param {any} message - The message to show.
 * @param {any} type - The notification type.
 */
const notify = (dispatch: any, message: string, type: 'success' | 'error') => {
  dispatch({
    type: 'notification/setNotificationMessage',
    payload: { status: true, message, type },
  });
};

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

interface EmspLocationsRequest extends ApiRequest {
  countryCode: string;
  partyId: string;
  counterPartyId?: string;
  counterCountryCode?: string;
}

interface FetchEmspLocationsResponse {
  results: any[];
  limit: number;
  page: number;
}

/**
 * Fetches a page of OCPI locations received from connected CPOs for an eMSP role.
 */
export const fetchEmspLocations = createAsyncThunk<
  FetchEmspLocationsResponse,
  EmspLocationsRequest
>(
  'emsp-locations/fetchEmspLocations',
  async (
    { countryCode, partyId, counterPartyId, counterCountryCode, page, limit },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const resolvedPage = page || DefaultApiParameters.page;
      const resolvedLimit = limit || DefaultApiParameters.limit;
      const response = await ocpiApi.getInternalEmspLocations(
        countryCode,
        partyId,
        counterPartyId || '',
        counterCountryCode || '',
        resolvedPage,
        resolvedLimit,
      );
      const results = response?.data?.data ?? response?.data ?? [];
      return {
        results: Array.isArray(results) ? results : [],
        limit: resolvedLimit,
        page: resolvedPage,
      };
    } catch (error: any) {
      notify(
        dispatch,
        error?.response?.data?.message || 'Failed to fetch received locations.',
        'error',
      );
      return rejectWithValue(error?.response?.data);
    }
  },
);

/**
 * Builds a user-facing error message for a failed pull request, distinguishing between
 * being offline, the partner not responding, and the partner returning an error response.
 * @param {any} error - The error thrown by the pull request.
 * @returns {string} A user-facing error message.
 */
const resolvePullErrorMessage = (error: any): string => {
  if (typeof window !== 'undefined' && !window.navigator.onLine) {
    return 'You appear to be offline. Check your internet connection and try again.';
  }
  if (error?.response) {
    return (
      error.response.data?.message ||
      error.response.data?.status_message ||
      'Failed to pull locations from the connected CPO.'
    );
  }
  if (error?.request) {
    return 'No response received from the connected CPO. Please try again later.';
  }
  return error?.message || 'Failed to pull locations from the connected CPO.';
};

/**
 * Manually pulls OCPI locations from a connected CPO partner for an eMSP role.
 */
export const pullEmspLocations = createAsyncThunk<
  { message: string },
  {
    countryCode: string;
    partyId: string;
    counterPartyId?: string;
    counterCountryCode?: string;
    connectionId?: string;
  }
>(
  'emsp-locations/pullEmspLocations',
  async (
    { countryCode, partyId, counterPartyId, counterCountryCode, connectionId },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const response = await ocpiApi.pullInternalEmspLocations(
        countryCode,
        partyId,
        counterPartyId,
        counterCountryCode,
        connectionId,
      );
      const message =
        response?.data?.message ||
        response?.data?.status_message ||
        'Locations successfully pulled.';
      return { message };
    } catch (error: any) {
      const message = resolvePullErrorMessage(error);
      notify(dispatch, message, 'error');
      return rejectWithValue(message);
    }
  },
);

/**
 * Resolves the filter list for the eMSP locations table. Filter options are known upfront
 * (there is no backend filter-list endpoint for OCPI locations), so this only exists to
 * satisfy CustomTable's filterListToDispatch contract.
 */
export const fetchEmspLocationFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'emsp-locations/fetchEmspLocationFilters',
  async () => availableFilters,
);

interface EMSPLocationsInitialState {
  locations: any[];
  limit: number;
  page: number;
  totalPages: number;
  totalResults: number;
  loading: boolean;
  filters: CommonFilterProperties[];
  filterListLoading: boolean;
  isFilterUpdated: boolean;
  sortBy: string;
  sortField: string;
}

const initialState: EMSPLocationsInitialState = {
  locations: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

export const emspLocationsSlice = createSlice({
  name: 'emsp-locations',
  initialState,
  reducers: {
    /**
     * Update eMSP Locations filters
     * @param {any} state - The current slice state.
     * @param {any} action - The action payload containing filter updates.
     */
    updateEMSPLocationsFilters: (state: any, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state - The current slice state.
     */
    disableEmspLocationsFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param {any} state - The current slice state.
     * @param {any} action - The action payload containing sorting criteria.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Extra reducers to handle thunk requests.
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmspLocations.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchEmspLocations.fulfilled, (state, action) => {
        state.loading = false;
        state.locations = action.payload.results;
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalResults = action.payload.results.length;
        state.totalPages = Math.max(
          1,
          Math.ceil(action.payload.results.length / action.payload.limit),
        );
      })
      .addCase(fetchEmspLocations.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchEmspLocationFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(fetchEmspLocationFilters.fulfilled, (state, action) => {
        state.filterListLoading = false;
        state.filters = action.payload;
      })
      .addCase(fetchEmspLocationFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

export const { updateEMSPLocationsFilters, disableEmspLocationsFilterApplied, setSortData } =
  emspLocationsSlice.actions;

export default emspLocationsSlice.reducer;
